// src/components/waypoint/BriefScreen.tsx

import { ArrowRight } from "lucide-react";
import { useState } from "react";
import type { WaypointSessionDetail } from "@/hooks/use-waypoint-sessions";

type Props = {
  session: WaypointSessionDetail;
  onReady: (notes: string, stream: MediaStream | undefined) => void;
};

const ROLE_LABELS: Record<string, string> = {
  primary: "Decision-maker",
  supporting: "Stakeholder",
  observer: "Observer",
};

const AVATAR_COLORS = ["#2D9BD6", "#407EC1", "#2EA86A", "#9B5DE5", "#E8A93C"];

function initials(name: string): string {
  return name
    .split(" ")
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();
}

export function BriefScreen({ session, onReady }: Props) {
  const [notes, setNotes] = useState("");

  async function handleEnter() {
    let stream: MediaStream | undefined;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
    } catch {
      // permission denied or unavailable — CallScreen will handle the muted state
    }
    onReady(notes, stream);
  }

  const scen = session.scenario;
  const brief = scen?.briefVariants[session.seed.briefVariantIndex];

  return (
    <div
      style={{
        height: "100vh",
        overflow: "hidden",
        background: "radial-gradient(120% 80% at 50% 0%, #0E2658 0%, #001027 55%, #00081A 100%)",
        display: "flex",
        flexDirection: "column",
        padding: "24px 40px 28px",
        color: "#E6EEF8",
        boxSizing: "border-box",
      }}
    >
      {/* ── Header (full width) ─────────────────────────────────────────────── */}
      <div style={{ flexShrink: 0, marginBottom: 20 }}>
        <div style={{ marginBottom: 14 }}>
          <span
            style={{
              fontSize: 10,
              fontWeight: 700,
              textTransform: "uppercase" as const,
              letterSpacing: "0.14em",
              color: "#7FC2EC",
              fontFamily: "'Montserrat', sans-serif",
              background: "rgba(127,194,236,0.12)",
              border: "1px solid rgba(127,194,236,0.2)",
              padding: "4px 12px",
              borderRadius: 999,
            }}
          >
            Pre-call brief
          </span>
        </div>
        <h1
          style={{
            fontFamily: "'Montserrat', sans-serif",
            fontWeight: 800,
            fontSize: 28,
            color: "#FAFBFD",
            margin: "0 0 5px",
          }}
        >
          {scen?.name ?? "Loading…"}
        </h1>
        {brief && (
          <p style={{ fontSize: 13.5, color: "rgba(230,238,248,0.55)", margin: 0 }}>
            {brief.company} · {brief.industry.replace("_", " ")}
          </p>
        )}
      </div>

      {/* ── Two-column grid ─────────────────────────────────────────────────── */}
      <div
        style={{
          flex: 1,
          overflow: "hidden",
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 24,
          minHeight: 0,
        }}
      >
        {/* Left column: situation + win conditions */}
        <div style={{ overflowY: "auto", paddingRight: 4 }}>
          {/* Situation card */}
          {brief && (
            <div
              style={{
                background: "rgba(255,255,255,0.04)",
                border: "1px solid rgba(127,194,236,0.12)",
                borderRadius: 14,
                padding: "18px 22px",
                marginBottom: 14,
              }}
            >
              <p
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  textTransform: "uppercase" as const,
                  letterSpacing: "0.14em",
                  color: "#7FC2EC",
                  margin: "0 0 12px",
                  fontFamily: "'Montserrat', sans-serif",
                }}
              >
                The situation
              </p>
              <p style={{ fontSize: 13.5, lineHeight: 1.7, color: "#D6E4F5", margin: "0 0 16px" }}>
                {brief.situation}
              </p>

              <div style={{ borderLeft: "2px solid #E8A93C", paddingLeft: 14, marginBottom: 14 }}>
                <p
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    textTransform: "uppercase" as const,
                    letterSpacing: "0.12em",
                    color: "#E8A93C",
                    margin: "0 0 5px",
                    fontFamily: "'Montserrat', sans-serif",
                  }}
                >
                  The tension
                </p>
                <p style={{ fontSize: 13.5, lineHeight: 1.6, color: "#D6E4F5", margin: 0 }}>
                  {brief.tension}
                </p>
              </div>

              <div style={{ borderLeft: "2px solid #2EA86A", paddingLeft: 14 }}>
                <p
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    textTransform: "uppercase" as const,
                    letterSpacing: "0.12em",
                    color: "#2EA86A",
                    margin: "0 0 5px",
                    fontFamily: "'Montserrat', sans-serif",
                  }}
                >
                  Your objective
                </p>
                <p style={{ fontSize: 13.5, lineHeight: 1.6, color: "#D6E4F5", margin: 0 }}>
                  {brief.objective}
                </p>
              </div>
            </div>
          )}

          {/* Win conditions */}
          {scen && scen.winConditions.length > 0 && (
            <div
              style={{
                background: "rgba(46,168,106,0.06)",
                border: "1px solid rgba(46,168,106,0.18)",
                borderRadius: 12,
                padding: "14px 18px",
              }}
            >
              <p
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  textTransform: "uppercase" as const,
                  letterSpacing: "0.14em",
                  color: "#2EA86A",
                  margin: "0 0 10px",
                  fontFamily: "'Montserrat', sans-serif",
                }}
              >
                Win conditions
              </p>
              <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
                {scen.winConditions.map((wc) => (
                  <div key={wc} style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                    <span
                      style={{ color: "#2EA86A", flexShrink: 0, marginTop: 1, fontWeight: 700 }}
                    >
                      ✓
                    </span>
                    <span style={{ fontSize: 13, color: "#D6E4F5", lineHeight: 1.55 }}>{wc}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right column: personas + scratchpad + CTA */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            gap: 14,
          }}
        >
          {/* In the room */}
          {scen && scen.roomRoles.length > 0 && (
            <div style={{ flexShrink: 0 }}>
              <p
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  textTransform: "uppercase" as const,
                  letterSpacing: "0.14em",
                  color: "rgba(230,238,248,0.45)",
                  margin: "0 0 8px",
                  fontFamily: "'Montserrat', sans-serif",
                }}
              >
                In the room
              </p>
              <div style={{ display: "flex", flexWrap: "wrap" as const, gap: 6 }}>
                {scen.roomRoles.map((role, i) => {
                  const p = scen.personaMap[role.personaSlug];
                  const avColor = AVATAR_COLORS[i % AVATAR_COLORS.length] ?? "#2D9BD6";
                  return (
                    <div
                      key={role.personaSlug}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 7,
                        background: "rgba(255,255,255,0.04)",
                        border: "1px solid rgba(127,194,236,0.10)",
                        borderRadius: 999,
                        padding: "5px 12px 5px 5px",
                      }}
                    >
                      <div
                        style={{
                          width: 24,
                          height: 24,
                          borderRadius: 999,
                          background: avColor,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontFamily: "'Montserrat', sans-serif",
                          fontWeight: 700,
                          fontSize: 9,
                          color: "#fff",
                          flexShrink: 0,
                        }}
                      >
                        {p ? initials(p.name) : "?"}
                      </div>
                      <span
                        style={{
                          fontSize: 12,
                          fontWeight: 600,
                          color: "#D6E4F5",
                          fontFamily: "'Raleway', sans-serif",
                          whiteSpace: "nowrap" as const,
                        }}
                      >
                        {p?.name ?? role.personaSlug}
                      </span>
                      <span
                        style={{
                          fontSize: 9,
                          fontWeight: 700,
                          color: "#7FC2EC",
                          textTransform: "uppercase" as const,
                          letterSpacing: "0.07em",
                          fontFamily: "'Montserrat', sans-serif",
                          opacity: 0.7,
                        }}
                      >
                        {ROLE_LABELS[role.role] ?? role.role}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Scratchpad — grows to fill remaining space */}
          <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
            <p
              style={{
                fontSize: 10,
                fontWeight: 700,
                textTransform: "uppercase" as const,
                letterSpacing: "0.14em",
                color: "rgba(230,238,248,0.45)",
                margin: "0 0 10px",
                fontFamily: "'Montserrat', sans-serif",
                flexShrink: 0,
              }}
            >
              Your notes
            </p>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Jot down your strategy, questions, or talking points…"
              style={{
                flex: 1,
                width: "100%",
                background: "rgba(255,255,255,0.04)",
                border: "1px solid rgba(127,194,236,0.12)",
                borderRadius: 12,
                padding: "14px 16px",
                color: "#D6E4F5",
                fontSize: 13.5,
                lineHeight: 1.65,
                resize: "none" as const,
                outline: "none",
                fontFamily: "'Raleway', sans-serif",
                boxSizing: "border-box" as const,
                transition: "border-color 0.15s",
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = "rgba(127,194,236,0.35)";
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = "rgba(127,194,236,0.12)";
              }}
            />
            <p
              style={{
                fontSize: 11,
                color: "rgba(230,238,248,0.3)",
                margin: "5px 0 0",
                flexShrink: 0,
              }}
            >
              Visible to you during the call.
            </p>
          </div>

          {/* CTA */}
          <div style={{ flexShrink: 0, display: "flex", justifyContent: "flex-end" }}>
            <button
              type="button"
              onClick={handleEnter}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "13px 26px",
                background: "linear-gradient(90deg, #1E6CC7, #2D9BD6)",
                border: "none",
                borderRadius: 12,
                cursor: "pointer",
                fontFamily: "'Montserrat', sans-serif",
                fontWeight: 700,
                fontSize: 14,
                color: "#fff",
                boxShadow: "0 8px 24px rgba(30,108,199,0.35)",
                transition: "opacity 0.2s",
              }}
            >
              Enter the room
              <ArrowRight style={{ width: 17, height: 17 }} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
